import { useBarcode } from "react-barcodes";
import {
  isAddressComplete,
  isMunicipalVio,
  isTicketFw,
  isTicketWarning,
} from "../../../utils/utils";
import styles from "./TicketForm.module.css";
import moment from "moment";

const BarcodeGenerator = ({ ticketNumber }) => {
  const { inputRef } = useBarcode({
    value: ticketNumber,
    options: {
      format: "CODE39",
    },
  });
  return (
    <img alt="ticket number barcode" height="75%" width="95%" ref={inputRef} />
  );
};

const BallotBox = ({
  checked = false,
  large = false,
  small = false,
  padding = 0,
  label = "",
  labelBefore = false,
  labelAfter = false,
  className = "",
  lineHeight = "inherit",
}) => {
  const CustomLabel = ({ label }) => (
    <span style={{ fontSize: large ? "9pt" : small ? "5pt" : "7pt" }}>
      {label}
    </span>
  );

  const fontSize = large ? "14pt" : "11pt";
  return (
    <div
      className={className}
      style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
    >
      {!!label && labelBefore && <CustomLabel label={label} />}
      <span
        style={{ fontSize: fontSize, padding: padding, lineHeight: lineHeight }}
      >
        {checked ? "\u2611" : "\u2610"}
      </span>
      {!!label && labelAfter && <CustomLabel label={label} />}
    </div>
  );
};

const TicketForm = ({ ticket, defendantCopy, showDialog, bac }) => {
  var currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  let waiverAmount = ticket?.penalty;

  let parentStreetAddress = "";
  let parentCity = "";
  let parentState = "";
  let parentZip = "";
  if (
    isAddressComplete(
      ticket?.defendantParent?.personalAttribute?.mailingAddress
    )
  ) {
    parentStreetAddress = `${
      ticket?.defendantParent?.personalAttribute?.mailingAddress?.streetNumber
    } ${
      ticket?.defendantParent?.personalAttribute?.mailingAddress?.streetName
    } ${
      ticket?.defendantParent?.personalAttribute?.mailingAddress?.unit
        ? "#" + ticket?.defendantParent?.personalAttribute?.mailingAddress?.unit
        : ""
    }`;
    parentCity = `${ticket?.defendantParent?.personalAttribute?.mailingAddress?.city}`;
    parentState = `${ticket?.defendantParent?.personalAttribute?.mailingAddress?.state}`;
    parentZip = `${ticket?.defendantParent?.personalAttribute?.mailingAddress?.zip}`;
  } else if (
    isAddressComplete(ticket?.defendantParent?.personalAttribute?.homeAddress)
  ) {
    parentStreetAddress = `${
      ticket?.defendantParent?.personalAttribute?.homeAddress?.streetNumber
    } ${ticket?.defendantParent?.personalAttribute?.homeAddress?.streetName} ${
      ticket?.defendantParent?.personalAttribute?.homeAddress?.unit
        ? "#" + ticket?.defendantParent?.personalAttribute?.homeAddress?.unit
        : ""
    }`;
    parentCity = `${ticket?.defendantParent?.personalAttribute?.homeAddress?.city}`;
    parentState = `${ticket?.defendantParent?.personalAttribute?.homeAddress?.state}`;
    parentZip = `${ticket?.defendantParent?.personalAttribute?.homeAddress?.zip}`;
  }

  const isVCVC = !isTicketWarning(ticket?.ticketViolationPrototype);
  const isFw = isTicketFw(ticket?.ticketViolationPrototype);
  const isMuni = isMunicipalVio(ticket?.ticketViolationPrototype);
  const isMale =
    ticket?.issuedTo?.personalAttribute?.gender?.name?.charAt(0) === "M";
  const isFemale =
    ticket?.issuedTo?.personalAttribute?.gender?.name?.charAt(0) === "F";
  const isCorpOrg = ticket?.issuedTo?.personalAttribute?.organization;
  const isOther =
    !ticket?.issuedTo?.personalAttribute?.organization &&
    ticket?.issuedTo?.personalAttribute?.gender?.name?.charAt(0) !== "M" &&
    ticket?.issuedTo?.personalAttribute?.gender?.name?.charAt(0) !== "F";

  const actions = ticket?.defendantActions;
  const actionSize =
    actions && actions.length > 200
      ? actions.length > 265
        ? "smallerData"
        : "smallData"
      : "data";

  const defendantIsNotActiveDuty =
    ticket?.reliefActType?.name ===
    "Defendant said he/she is NOT on active duty in the US armed forces";
  const defendantIsUnder17 =
    ticket?.reliefActType?.name === "Defendant is under 17 years of age";
  const defendantIsOrg =
    ticket?.reliefActType?.name ===
    "Defendant is an organization or a corporation";
  const defendantIsActiveDuty =
    ticket?.reliefActType?.name ===
    "Defendant said he/she IS on active duty or is scheduled to be on active duty in the U.S. armed forces.";
  return (
    <div
      className={
        showDialog ? styles["modalVerticalGrid"] : styles["verticalGrid"]
      }
    >
      <div className={styles["header"]}>
        <p style={{}}>VERMONT CIVIL VIOLATION, FISH AND WILDFLIFE</p>
        <p>VIOLATION, MUNICIPAL VIOLATION</p>
      </div>
      <div className={`${styles["cdl"]} ${styles["evenCenteredFlexRow"]}`}>
        <BallotBox padding={"3pt"} large checked={ticket?.cdl} />{" "}
        <span>CDL</span>
      </div>
      <div className={styles["licenseState"]}>
        <span>License&nbsp;State</span>
        <br />
        <span className={styles["data"]}>{ticket?.state?.name}</span>
      </div>
      <div className={styles["ticketNumberContainer"]}>
        {ticket?.ticketNumber && (
          <BarcodeGenerator ticketNumber={ticket?.ticketNumber} />
        )}
        <div className={styles["ticketNumberCheckboxes"]}>
          <BallotBox large checked={isVCVC} labelBefore label="VCVC" />
          <BallotBox large checked={isFw} labelBefore label="F&W" />
          <BallotBox large checked={isMuni} labelBefore label="Municipal" />
        </div>
      </div>
      <div className={styles["form"]}>
        <span className={""}>Form&nbsp;500</span>
        <br />
        <span className={""}>Rev.&nbsp;8/2023</span>
      </div>
      <div className={styles["emptyForm1"]}>
        <div className={styles["blackBox"]}></div>
      </div>
      <div className={styles["emptyForm2"]}></div>
      <div className={styles["driversLicenseNumber"]}>
        <span>Driver License Number</span>
        <br />
        <span className={styles["data"]}>{ticket?.driversLicense}</span>
      </div>
      <div className={styles["otherLicenseNumber"]}>
        <span>Hunting/Fishing/Trapping License Number</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.issuedTo?.personalAttribute?.fishWildlifeLicense}
        </span>
      </div>
      <div className={styles["lastName"]}>
        <span>Defendant Last or Organization Name</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.issuedTo?.personalAttribute?.lastName}
        </span>
      </div>
      <div className={styles["firstName"]}>
        <span>First</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.issuedTo?.personalAttribute?.firstName}
        </span>
      </div>
      <div className={styles["middleInitial"]}>
        <span>Mid.&nbsp;Initial</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.issuedTo?.personalAttribute?.middleName?.charAt(0)}
        </span>
      </div>
      <div className={styles["mailingAddress"]}>
        <span>Defendant Current Mailing Address</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.issuedToAddress?.streetNumber}{" "}
          {ticket?.issuedToAddress?.streetName}{" "}
          {ticket?.issuedToAddress?.unit
            ? "#" + ticket?.issuedToAddress?.unit
            : ""}
        </span>
      </div>
      <div className={styles["homePhone"]}>
        <span>Home Phone</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.issuedTo?.personalAttribute?.phone ||
            `            -                -     `}
        </span>
      </div>
      <div className={styles["city"]}>
        <span>City</span>
        <br />
        <span className={styles["data"]}>{ticket?.issuedToAddress?.city}</span>
      </div>
      <div className={styles["state"]}>
        <span>State</span>
        <br />
        <span className={styles["data"]}>{ticket?.issuedToAddress?.state}</span>
      </div>
      <div className={styles["zipCode"]}>
        <span>Zip Code</span>
        <br />
        <span className={styles["data"]}>{ticket?.issuedToAddress?.zip}</span>
      </div>
      <div className={styles["businessPhone"]}>
        <span>Business Phone</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.issuedTo?.personalAttribute?.cell ||
            `            -                -     `}
        </span>
      </div>
      <div className={styles["dob"]}>
        <span>Date of Birth</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.issuedTo?.personalAttribute?.dob
            ? moment
                .utc(
                  ticket?.issuedTo?.personalAttribute?.dob,
                  "YYYY-MM-DD HH:mm:ss UTC"
                )
                .local()
                .format("MM-DD-YYYY")
            : ""}
        </span>
      </div>
      <div className={styles["genderBorderBox"]}></div>
      <BallotBox
        large={showDialog}
        lineHeight={"7pt"}
        className={styles["maleCheckbox"]}
        checked={isMale}
        labelAfter
        label="Male"
      />
      <BallotBox
        large={showDialog}
        lineHeight={"7pt"}
        className={styles["corpOrgCheckbox"]}
        checked={isCorpOrg}
        labelAfter
        label="Corp/Org"
      />
      <BallotBox
        large={showDialog}
        lineHeight={"7pt"}
        className={styles["femaleCheckbox"]}
        checked={isFemale}
        labelAfter
        label="Female"
      />
      <BallotBox
        large={showDialog}
        lineHeight={"7pt"}
        className={styles["otherCheckbox"]}
        checked={isOther}
        labelAfter
        label="Other"
      />
      <div className={styles["pob"]}>
        <span className={styles["smallCenteredSpan"]}>
          Place&nbsp;of&nbsp;Birth
        </span>
      </div>
      <div className={styles["pobData"]}>
        <span className={styles["smallData"]}>
          {ticket?.issuedTo?.personalAttribute?.pobAddress?.name}
        </span>
      </div>
      <div className={styles["height"]}>
        <span className={styles["smallCenteredSpan"]}>Height</span>
      </div>
      <div className={styles["heightData"]}>
        <span className={styles["smallData"]}>
          {ticket?.issuedTo?.personalAttribute?.height}
        </span>
      </div>
      <div className={styles["weight"]}>
        <span className={styles["smallCenteredSpan"]}>Weight</span>
      </div>
      <div className={styles["weightData"]}>
        <span className={styles["smallData"]}>
          {ticket?.issuedTo?.personalAttribute?.weight}
        </span>
      </div>
      <div className={styles["hair"]}>
        <span className={styles["smallCenteredSpan"]}>Hair</span>
      </div>
      <div className={styles["hairData"]}>
        <span className={styles["smallData"]}>
          {ticket?.issuedTo?.personalAttribute?.hairColor?.name?.split(
            "/"
          )?.[0] || ""}
        </span>
      </div>
      <div className={styles["eyes"]}>
        <span className={styles["smallCenteredSpan"]}>Eyes</span>
      </div>
      <div className={styles["eyesData"]}>
        <span className={styles["smallData"]}>
          {ticket?.issuedTo?.personalAttribute?.eyeColor?.name?.split(
            "-"
          )?.[0] || ""}
        </span>
      </div>
      <div className={styles["vehicleRegistration"]}>
        <span>Vehicle or Boat Reg. Number:</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.vehicle?.vehiclePlate?.name}
        </span>
      </div>
      <div className={styles["vehicleState"]}>
        <span>State</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.vehicle?.vehicleState?.name}
        </span>
      </div>
      <div className={styles["vehicleYear"]}>
        <span>Year</span>
        <br />
        <span className={styles["data"]}>{ticket?.vehicle?.year}</span>
      </div>
      <div className={styles["vehicleMake"]}>
        <span>Make</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.vehicle?.vehicleMake?.name}
        </span>
      </div>
      <div className={styles["vehicleColor"]}>
        <span>Color</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.vehicle?.vehicleColor?.name}
        </span>
      </div>
      <div className={styles["vehicleModel"]}>
        <span>Model</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.vehicle?.vehicleModel?.name}
        </span>
      </div>
      <div className={styles["violationDate"]}>
        <span>Violation Date</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.violationDate
            ? moment
                .utc(ticket?.violationDate, "YYYY-MM-DD HH:mm:ss UTC")
                .local()
                .format("MM-DD-YYYY")
            : ""}
        </span>
      </div>
      <div className={styles["time"]}>
        <span>Time</span>
        <br />
        <span className={styles["data"]}>
          {ticket?.violationDate
            ? moment
                .utc(ticket?.violationDate, "YYYY-MM-DD HH:mm:ss UTC")
                .local()
                .format("HH:mm")
            : ""}
        </span>
      </div>
      <div className={styles["municipality"]}>
        <span>Municipality</span>
        <br />
        <span className={styles["data"]}>{ticket?.municipality?.name}</span>
      </div>
      <div className={styles["highway"]}>
        <span>Highway</span>
        <br />
        <span className={styles["data"]}>{ticket?.highway?.name}</span>
      </div>
      <div className={styles["landmark"]}>
        <span>Place or Landmark</span>
        <br />
        <span className={styles["data"]}>{ticket?.ticketLocation?.name}</span>
      </div>
      <div className={styles["violation"]}>
        <span>
          <strong>
            Defendant did then and there commit the following acts in violation
            of Vermont/local law:
          </strong>
        </span>
        <br />
        <span className={styles["data"]}>
          {ticket?.ticketViolationPrototype?.fullName}
        </span>
      </div>
      <div className={styles["violationType"]}>
        <span className={styles[actionSize]}>{ticket?.defendantActions}</span>
      </div>
      <div className={styles["alcoholLevel"]}>
        <span>Alcohol Lev.</span>
        <br />
        <div className={styles["rightAlign"]}>
          <span className={`${styles["data"]} ${styles["reset"]}`}>
            {bac} %&nbsp;
          </span>
        </div>
      </div>
      <div className={styles["actualSpeed"]}>
        <span>Actual Speed</span>
        <span className={styles["data"]}>
          {ticket?.actualSpeed ? ticket?.actualSpeed + " MPH" : "   MPH"}
        </span>
      </div>
      <div className={styles["postedLimit"]}>
        <span>Posted Limit</span>
        <span className={styles["data"]}>
          {ticket?.postedLimit ? ticket?.postedLimit + " MPH" : "   MPH"}
        </span>
      </div>
      <BallotBox
        large={showDialog}
        className={styles["accident"]}
        checked={ticket?.accident}
        label="Accident"
        labelAfter
      />
      <BallotBox
        large={showDialog}
        className={styles["fatality"]}
        checked={ticket?.fatality}
        label="Fatality"
        labelAfter
      />
      <BallotBox
        large={showDialog}
        className={styles["seatBeltVio"]}
        checked={!!ticket?.seatBeltOccurrence}
        label="Seat Belt Viol."
        labelAfter
      />
      <BallotBox
        large={showDialog}
        className={styles["commercialVehicle"]}
        checked={ticket?.commercialVehicle}
        label="Commercial Vehicle"
        labelAfter
      />
      <BallotBox
        large={showDialog}
        className={styles["hazmat"]}
        checked={ticket?.hazmat}
        label="Hazardous Material"
        labelAfter
      />
      <div className={styles["municipalOrd"]}>
        <span>Municipal Ordinance</span>
        <span>{ticket?.municipalOrdinance}</span>
      </div>
      <div className={styles["inViolationOf"]}>
        <span className={styles["data"]}>
          In violation of {ticket?.ticketViolationPrototype?.title || ""}{" "}
          {ticket?.ticketViolationPrototype?.statute
            ?.split("")
            .join(".")
            .concat(".") || ""}{" "}
          &sect; {ticket?.ticketViolationPrototype?.section || ""}
        </span>
      </div>
      <div className={styles["section"]}>
        <span className={styles["data"]}>
          49 C.F.R. &sect; {ticket?.federalViolationCode?.name}
        </span>
      </div>
      <div className={styles["violationCode"]}>
        <span>Violation Code</span>
        <span className={styles["data"]}>{ticket?.code}</span>
      </div>
      <div className={styles["penaltiesHeader"]}>
        <span className={`${styles["rotated"]} ${styles["reset"]}`}>
          PENALTIES
        </span>
      </div>
      <div className={styles["denied"]}>
        If you <strong>REQUEST A HEARING</strong>
        <br />
        and the State proves the <br />
        violation, the penalty must be
        <br />
        within the penalty range. <br />
        Commonly, the waiver amount
        <br />
        plus <strong>$65.00</strong> in court costs are
        <br />
        assessed.
        <br />
      </div>
      <div className={styles["points"]}>
        <strong>POINTS</strong>
        <span className={`${styles["data"]} ${styles["endOfSpan"]}`}>
          {ticket?.points}
        </span>
      </div>
      <div className={styles["penaltyRange"]}>
        <strong>PENALTY RANGE</strong>
      </div>
      <div className={styles["min"]}>
        <strong>MINIMUM&nbsp;$</strong>
        <span className={`${styles["data"]} ${styles["endOfSpan"]}`}>
          {ticket?.penaltyMin}
        </span>
      </div>
      <div className={styles["max"]}>
        <strong>MAXIMUM&nbsp;$ </strong>
        <span className={`${styles["data"]} ${styles["endOfSpan"]}`}>
          {ticket?.penaltyMax}
        </span>
      </div>
      <div className={styles["restitution"]}>
        <strong>RESTITUTION&nbsp;(F&W)&nbsp;+</strong>
      </div>
      <div className={`${styles["vertSpacing"]} ${styles["admitted"]}`}>
        <span className={styles["reset"]}>
          If you <strong>DO NOT WANT A HEARING,</strong>
          <br />
          you may pay the waiver amount
          <br />
          instead of appearing in court.
        </span>
        <br />
        <div className={styles["waiverBox"]}>
          <strong>
            WAIVER
            <br /> AMOUNT
          </strong>
          <div className={styles["arrow"]}>
            <div className={styles["arrow-point"]}></div>
          </div>
          <span className={`${styles["data"]}`}>
            {waiverAmount ? currencyFormatter.format(waiverAmount) : "NA"}
          </span>
        </div>
      </div>
      <div className={styles["assertion"]}>
        <span>
          I have just and reasonable grounds to believe the person named above
          committed this violation.
        </span>
      </div>
      <div className={styles["deliveredTo"]}>
        <span>Delivered To (Defendant, Reg. Agent, or Corp. Officer)</span>
        <span className={styles["data"]}>{ticket?.deliveredToType?.name}</span>
      </div>
      <div className={styles["dateServed"]}>
        <span>Date Served</span>
        <span className={styles["data"]}>
          {ticket?.issuedAt
            ? moment
                .utc(ticket?.issuedAt, "YYYY-MM-DD HH:mm:ss UTC")
                .local()
                .format("MM-DD-YYYY")
            : ""}
        </span>
      </div>
      <div className={styles["deliveryType"]}>
        <BallotBox
          large={showDialog}
          checked={ticket?.ticketDeliveryType?.name === "Hand"}
          label="In Hand"
          labelAfter
        />
        <BallotBox
          large={showDialog}
          checked={ticket?.ticketDeliveryType?.name === "Mail"}
          label="US Mail"
          labelAfter
        />
      </div>
      <div className={styles["officerNumber"]}>
        <span>Officer No.</span>
        <span className={styles["data"]}>
          {ticket?.officer?.deptAttribute.stateIdNumber || ""}
        </span>
      </div>
      <div className={styles["officerName"]}>
        <span>Officer Name (Printed)</span>
        <span className={styles["data"]}>
          {ticket?.officer?.deptAttribute?.badge
            ? ticket?.officer?.deptAttribute?.badge + ":"
            : ""}{" "}
          {ticket?.officer?.personalAttribute?.lastName}
          {ticket?.officer?.personalAttribute?.firstName
            ? ", " + ticket?.officer?.personalAttribute?.firstName
            : ""}
        </span>
      </div>
      <div className={styles["officerSignature"]}>
        <span>Officer Signature</span>
        <span className={`${styles["data"]} ${styles["cursive"]}`}>
          {ticket?.officer?.personalAttribute?.lastName
            ? ticket?.officer?.personalAttribute?.lastName + ","
            : ""}{" "}
          {ticket?.officer?.personalAttribute?.firstName}
        </span>
      </div>
      <div className={styles["departmentNumber"]}>
        <span>Dept No.</span>
        <span className={styles["data"]}>
          {ticket?.officer?.deptAttribute?.ori?.municipalityNumber || ""}
        </span>
      </div>
      <div className={styles["departmentName"]}>
        <span>Department Name</span>
        <span className={styles["data"]}>
          {ticket?.officer?.deptAttribute?.ori?.oriName}
        </span>
      </div>
      <div className={styles["relatedCrimCharge"]}>
        {/* <span>[{ticket?.relativeCrimeCharge ? "X" : " "}] Related criminal charge</span> */}
        <BallotBox
          large={showDialog}
          checked={ticket?.relativeCrimeCharge}
          label="Related criminal charge"
          labelAfter
        />
      </div>
      <div className={styles["guardianLastName"]}>
        <span>Parent or Guardian Last Name</span>
        <span className={styles["data"]}>
          {ticket?.defendantParent?.personalAttribute?.lastName}
        </span>
      </div>
      <div className={styles["guardianFirstName"]}>
        <span>First Name</span>
        <span className={styles["data"]}>
          {ticket?.defendantParent?.personalAttribute?.firstName}
        </span>
      </div>
      <div className={styles["guardianStreet"]}>
        <span>Street Address</span>
        <span className={styles["data"]}>{parentStreetAddress}</span>
      </div>
      <div className={styles["guardianCity"]}>
        <span>City</span>
        <span className={styles["data"]}>{parentCity}</span>
      </div>
      <div className={styles["guardianState"]}>
        <span>State</span>
        <span className={styles["data"]}>{parentState}</span>
      </div>
      <div className={styles["guardianZip"]}>
        <span>Zip Code</span>
        <span className={styles["data"]}>{parentZip}</span>
      </div>
      <div className={styles["reliefAct"]}>
        <span className={`${styles["serviceMembers"]}`}>
                        Servicemembers&apos; Civil Relief Act Declaration: Signed under penalty of
          perjury, I state:
        </span>
        <br />
        <div style={{ paddingTop: "2pt" }}>
          <BallotBox
            small={!showDialog}
            className={styles["list"]}
            large={showDialog}
            lineHeight={showDialog ? "12pt" : "6pt"}
            checked={defendantIsNotActiveDuty}
            labelAfter
            label="Defendant said he/she is NOT on active duty in the U.S. armed forces."
          />
          <BallotBox
            small={!showDialog}
            className={styles["list"]}
            large={showDialog}
            lineHeight={showDialog ? "12pt" : "6pt"}
            checked={defendantIsUnder17}
            labelAfter
            label="Defendant is under 17 years of age."
          />
          <BallotBox
            small={!showDialog}
            className={styles["list"]}
            large={showDialog}
            lineHeight={showDialog ? "12pt" : "6pt"}
            checked={defendantIsOrg}
            labelAfter
            label="Defendant is a business or corporation."
          />
          <BallotBox
            small={!showDialog}
            className={styles["list"]}
            large={showDialog}
            lineHeight={showDialog ? "12pt" : "6pt"}
            checked={defendantIsActiveDuty}
            labelAfter
            label="Defendant said he/she IS on active duty or is scheduled to be on active duty in the U.S. armed forces."
          />
        </div>
        <br />
        <div className={styles["bottomSig"]}>
          <span>Officer&nbsp;Signature: </span>
          <span className={`${styles["data"]} ${styles["cursive"]}`}>
            {ticket?.officer?.personalAttribute?.lastName},{" "}
            {ticket?.officer?.personalAttribute?.firstName}
          </span>
        </div>
      </div>
      <div className={styles["emptyCellAboveCopy"]}></div>
      <div className={styles["copy"]}>
        <span className={styles["bold"]}>
          {defendantCopy ? "Defendant" : "Officer"}
        </span>
        <span className={styles["bold"]}>Copy</span>
      </div>
    </div>
  );
};
export default TicketForm;
